import firebase from "firebase/app";
import "firebase/firestore";

import firestore from "src/apiService/firestore";

import type { IsRunningKey } from "../types";

/** Mutex TTL for client sync locks. Matches backend `runIntegrationCriticalSection`. */
export const SyncLockTtlMs = 10 * 60 * 1000;
/**
 * Shorter lease so abandoned direct imports (Vinted, Depop) surface for resume
 * sooner. Prefer this name in new code; `VintedSyncLockTtlMs` is the same value.
 */
export const DirectImportSyncLockTtlMs = 5 * 60 * 1000;
export const VintedSyncLockTtlMs = DirectImportSyncLockTtlMs;
export const SyncLockHeartbeatMs = 10 * 1000;

export class LostSyncLockError extends Error {
  constructor(message = "Lost sync lock") {
    super(message);
    this.name = "LostSyncLockError";
  }
}

export interface SyncLock {
  owner: string;
  /** Aborted when Firestore heartbeat loses the lease (not the Web Lock). */
  signal: AbortSignal;
  /** Throws LostSyncLockError if the Firestore lease was lost (see signal). */
  throwIfAborted(): void;
}

export interface SyncLockOptions {
  getDb?: () => firebase.firestore.Firestore;
  requestLock?: NavigatorLocks["request"];
  now?: () => number;
  randomUUID?: () => string;
  heartbeatMs?: number;
  ttlMs?: number;
  /**
   * When set, skip acquire and run under this pre-claimed owner (e.g. resume
   * after claimExpiredLease). Heartbeat / clear still use this owner.
   */
  claimedOwner?: string;
  /** Called when the run stops because the Firestore lease was lost. */
  onLostLock?: (info: { integrationId: string; keys: IsRunningKey[] }) => void;
}

interface NavigatorLocks {
  request: (
    name: string,
    options: { mode: "exclusive"; ifAvailable?: boolean },
    callback: (lock: { name: string } | null) => Promise<boolean>
  ) => Promise<boolean>;
}

function ownerField(
  key: IsRunningKey
): "salesIsRunningOwner" | "inventoryIsRunningOwner" {
  return key === "salesIsRunning"
    ? "salesIsRunningOwner"
    : "inventoryIsRunningOwner";
}

/** One Web Lock per integration — same-browser syncs queue here (FIFO). */
export function webLockName(integrationId: string) {
  return `mrg-sync-${integrationId}`;
}

function createOwnerId(randomUUID?: () => string) {
  if (randomUUID) return randomUUID();
  if (typeof crypto !== "undefined" && crypto.randomUUID)
    return crypto.randomUUID();
  return `${Date.now()}-${Math.random().toString(36).slice(2, 11)}`;
}

async function acquireFirestoreLease(
  db: firebase.firestore.Firestore,
  integrationId: string,
  keys: IsRunningKey[],
  owner: string,
  now: number,
  ttlMs: number
): Promise<boolean> {
  return await db.runTransaction(async (t) => {
    const ref = db.collection("Plaid_Integrations").doc(integrationId);
    const doc = await t.get(ref);
    if (!doc.exists)
      throw new Error(`Integration ${integrationId} doesn't exist`);
    const data = doc.data();
    if (!data) throw new Error(`Integration ${integrationId} doesn't exist`);

    const update: Record<string, unknown> = {};
    for (const key of keys) {
      if (!key) continue;
      if (data[key] && data[key] + ttlMs > now) return false;
      update[key] = now;
      update[ownerField(key)] = owner;
    }

    if (Object.keys(update).length <= 0) return false;

    await t.update(ref, update);
    return true;
  });
}

async function refreshFirestoreLease(
  db: firebase.firestore.Firestore,
  integrationId: string,
  keys: IsRunningKey[],
  owner: string,
  now: number
): Promise<boolean> {
  return await db.runTransaction(async (t) => {
    const ref = db.collection("Plaid_Integrations").doc(integrationId);
    const doc = await t.get(ref);
    if (!doc.exists) return false;
    const data = doc.data();
    if (!data) return false;

    const update: Record<string, unknown> = {};
    for (const key of keys) {
      if (!key) continue;
      // Another owner stole the lease — do not refresh timestamps.
      if (data[ownerField(key)] !== owner) return false;
      update[key] = now;
    }

    if (Object.keys(update).length <= 0) return false;

    await t.update(ref, update);
    return true;
  });
}

async function clearLeaseIfOwner(
  db: firebase.firestore.Firestore,
  integrationId: string,
  keys: IsRunningKey[],
  owner: string
): Promise<void> {
  await db.runTransaction(async (t) => {
    const ref = db.collection("Plaid_Integrations").doc(integrationId);
    const doc = await t.get(ref);
    if (!doc.exists) return;
    const data = doc.data();
    if (!data) return;

    const update: Record<string, unknown> = {};
    for (const key of keys) {
      if (!key) continue;
      if (data[ownerField(key)] !== owner) continue;
      update[key] = firebase.firestore.FieldValue.delete();
      update[ownerField(key)] = firebase.firestore.FieldValue.delete();
    }

    if (Object.keys(update).length) await t.update(ref, update);
  });
}

/** Clear Firestore lease fields only when `owner` still owns them. */
export async function releaseSyncLeaseIfOwner(
  integrationId: string,
  keys: IsRunningKey[],
  owner: string,
  options: Pick<SyncLockOptions, "getDb"> = {}
): Promise<void> {
  const getDb = options.getDb || firestore;
  await clearLeaseIfOwner(getDb(), integrationId, keys, owner);
}

async function runWithFirestoreLease(
  integrationId: string,
  keys: IsRunningKey[],
  run: (lock: SyncLock) => Promise<void>,
  options: SyncLockOptions
): Promise<boolean> {
  const getDb = options.getDb || firestore;
  const nowFn = options.now || Date.now;
  const ttlMs = options.ttlMs ?? SyncLockTtlMs;
  const heartbeatMs = options.heartbeatMs ?? SyncLockHeartbeatMs;
  const owner = options.claimedOwner || createOwnerId(options.randomUUID);
  const db = getDb();

  if (!options.claimedOwner) {
    const acquired = await acquireFirestoreLease(
      db,
      integrationId,
      keys,
      owner,
      nowFn(),
      ttlMs
    );
    if (!acquired) return false;
  }

  const controller = new AbortController();
  const lock: SyncLock = {
    owner,
    signal: controller.signal,
    /**
     * Cooperative stop after the Firestore heartbeat lost the lease (owner
     * mismatch / missing doc / refresh error). Not the Web Lock itself — the
     * Web Lock is held for the whole `run` callback. Heartbeat already refuses
     * to write when the owner no longer matches; it only aborts this signal.
     */
    throwIfAborted() {
      if (controller.signal.aborted) throw new LostSyncLockError();
    },
  };

  const interval = setInterval(() => {
    refreshFirestoreLease(getDb(), integrationId, keys, owner, nowFn())
      .then((ok) => {
        if (!ok) controller.abort();
      })
      .catch((error) => {
        console.warn("Sync lock heartbeat failed", error);
        controller.abort();
      });
  }, heartbeatMs);

  try {
    // Abort is cooperative: `throwIfAborted` in write loops stops work.
    // Do not return early while `run` is still in flight or lastSync / sale
    // writes can continue after the lease is released.
    //
    // No pagehide/unload listener: releasing the Firestore lease on hide would
    // stop a sync that was merely backgrounded. While the tab still owns the
    // lease (within TTL), heartbeats resume when timers unthrottle and the run
    // continues. Lease is cleared only in finally when this run finishes.
    await run(lock);
    return true;
  } catch (error) {
    if (error instanceof LostSyncLockError) {
      console.warn("Sync aborted; lost lock", { integrationId });
      options.onLostLock?.({ integrationId, keys });
      return true;
    }
    throw error;
  } finally {
    clearInterval(interval);
    await clearLeaseIfOwner(getDb(), integrationId, keys, owner);
  }
}

/**
 * Claim an expired (or missing) Firestore lease for resume. Returns null when
 * the lease is still fresh (or the doc is missing) — in that case the
 * transaction writes nothing, so there is no lock to clean up. On success the
 * caller must run under `withSyncLock(..., { claimedOwner })` (or
 * `releaseSyncLeaseIfOwner`) so the stamp is not left orphaned.
 */
export async function claimExpiredLease(
  integrationId: string,
  keys: IsRunningKey[],
  options: Pick<SyncLockOptions, "getDb" | "now" | "randomUUID" | "ttlMs"> = {}
): Promise<string | null> {
  const getDb = options.getDb || firestore;
  const nowFn = options.now || Date.now;
  const ttlMs = options.ttlMs ?? SyncLockTtlMs;
  const owner = createOwnerId(options.randomUUID);
  const db = getDb();
  const now = nowFn();

  return await db.runTransaction(async (t) => {
    const ref = db.collection("Plaid_Integrations").doc(integrationId);
    const doc = await t.get(ref);
    if (!doc.exists) return null;
    const data = doc.data();
    if (!data) return null;

    const update: Record<string, unknown> = {};
    for (const key of keys) {
      if (!key) continue;
      const ts = data[key];
      if (ts && ts + ttlMs > now) return null;
      update[key] = now;
      update[ownerField(key)] = owner;
    }

    if (Object.keys(update).length <= 0) return null;
    await t.update(ref, update);
    return owner;
  });
}

/**
 * Drop an expired (or missing) Firestore lease and any related fields, in one
 * transaction. Used to discard an abandoned run instead of resuming it.
 * Returns false when a lease is still fresh — a sync is running elsewhere, so
 * nothing is written.
 */
export async function clearExpiredLease(
  integrationId: string,
  keys: IsRunningKey[],
  options: Pick<SyncLockOptions, "getDb" | "now" | "ttlMs"> & {
    /** Extra document fields to delete alongside the lease. */
    alsoDelete?: string[];
  } = {}
): Promise<boolean> {
  const getDb = options.getDb || firestore;
  const nowFn = options.now || Date.now;
  const ttlMs = options.ttlMs ?? SyncLockTtlMs;
  const db = getDb();
  const now = nowFn();

  return await db.runTransaction(async (t) => {
    const ref = db.collection("Plaid_Integrations").doc(integrationId);
    const doc = await t.get(ref);
    if (!doc.exists) return false;
    const data = doc.data();
    if (!data) return false;

    const update: Record<string, unknown> = {};
    for (const key of keys) {
      if (!key) continue;
      const ts = data[key];
      if (ts && ts + ttlMs > now) return false;
      update[key] = firebase.firestore.FieldValue.delete();
      update[ownerField(key)] = firebase.firestore.FieldValue.delete();
    }
    for (const field of options.alsoDelete || []) {
      update[field] = firebase.firestore.FieldValue.delete();
    }

    if (Object.keys(update).length <= 0) return false;
    await t.update(ref, update);
    return true;
  });
}

/**
 * Acquire an exclusive Web Lock for this integration (same browser — queued
 * FIFO) then Firestore lease(s) for the given keys (cross-device mutex per
 * key). Returns false only when a Firestore lease cannot be acquired.
 */
export async function withSyncLock(
  integrationId: string,
  keys: IsRunningKey[],
  run: (lock: SyncLock) => Promise<void>,
  options: SyncLockOptions = {}
): Promise<boolean> {
  const requestLock =
    options.requestLock ||
    ((...args) => navigator.locks.request(...args) as Promise<boolean>);

  if (keys.filter(Boolean).length <= 0) return false;

  return await requestLock(
    webLockName(integrationId),
    { mode: "exclusive" },
    async (held) => {
      if (!held) return false;
      return await runWithFirestoreLease(integrationId, keys, run, options);
    }
  );
}
